import { z } from 'zod';
import { ComponentInstanceSchema } from '../types/DiagramComponentInstance.schema';
import { DiagramConnectionSchema } from '../types/DiagramConnection.schema';

export enum JobStatus {
    PENDING = 'pending',
    PROCESSING = 'processing',
    DONE = 'done',
    ERROR = 'error',
}

export const JOB_STATUS_OPTIONS = [
    {
        label: 'Pending',
        value: JobStatus.PENDING,
    },
    {
        label: 'Processing',
        value: JobStatus.PROCESSING,
    },
    {
        label: 'Done',
        value: JobStatus.DONE,
    },
    {
        label: 'Error',
        value: JobStatus.ERROR,
    },
];

export type JobLog = z.infer<typeof JobLogSchema>;

export const JobErrorSchema = z.object({
    type: z.string().optional().nullable(),
    message: z.string(),
    stackTrace: z.array(z.string()).optional().nullable(),
});

export const JobExecutionSchema = z.object({
    platform: z.string().optional().nullable(),
    requestId: z.string().optional().nullable(),
    gitCommit: z.string().optional().nullable(),
});

export const JobErrorResponseSchema = z.object({
    error: JobErrorSchema,
    execution: JobExecutionSchema,
});

export const JobErrorLogSchema = z.object({
    id: z.string().optional().nullable(),
    type: z.enum(['simulations', 'wireSizings', 'faultSimulations']),
    status: z.nativeEnum(JobStatus),
    error: JobErrorResponseSchema.optional().nullable(),
});

export const JobSchema = z.object({
    id: z.string().default(''),
    name: z.string(),
    status: z.nativeEnum(JobStatus),
    result: z.object({}),
    diagram: z.string(),
    project: z.string(),
    createdBy: z.string(),
    createdAt: z.string(),
    updatedAt: z.string().optional(),
    error: JobErrorSchema.optional().nullable(),
    execution: JobExecutionSchema.optional().nullable(),
});

export const JobLogSchema = z.object({
    parentJobId: z.string().optional(),
    parentJobType: z.enum(['simulations', 'wireSizings', 'faultSimulations']).optional(),
    jobId: z.string().optional().nullable(),
    type: z.enum(['simulations', 'wireSizings', 'faultSimulations']),
    message: z.string(),
    zoneIndex: z.number().optional().nullable(),
    error: JobErrorSchema.optional().nullable(),
    execution: JobExecutionSchema.optional().nullable(),
});

export const JobDiagramSnapshotSchema = z.object({
    componentInstances: z.record(ComponentInstanceSchema.shape.id, z.unknown()),
    connections: z.record(DiagramConnectionSchema.shape.id, z.unknown()),
});

export type JobErrorResponse = z.infer<typeof JobErrorResponseSchema>;
export type JobErrorLog = z.infer<typeof JobErrorLogSchema>;
export type JobDiagramSnapshot = z.infer<typeof JobDiagramSnapshotSchema>;
export type JobError = z.infer<typeof JobErrorSchema>;
